import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Layout } from "@/components/layout/Layout";
import { COOKIE_SETTINGS_EVENT } from "@/components/common/CookieConsent";
import { contactLabels, legalDocuments } from "@/data/legal";

interface LegalPageProps {
  doc: keyof typeof legalDocuments;
}

export function LegalPage({ doc }: LegalPageProps) {
  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith("en") ? "en" : "he";
  const content = legalDocuments[doc][lang];
  const labels = contactLabels[lang];

  return (
    <Layout>
      <Helmet>
        <html lang={lang} dir={lang === "he" ? "rtl" : "ltr"} />
        <title>{content.metaTitle}</title>
        <meta name="description" content={content.metaDescription} />
        <link rel="canonical" href={content.canonical} />
        <meta property="og:title" content={content.metaTitle} />
        <meta property="og:description" content={content.metaDescription} />
        <meta property="og:url" content={content.canonical} />
        <meta property="og:type" content="website" />
      </Helmet>

      <section className="pt-32 pb-20 bg-background">
        <div className="container px-4 max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <h1 className="font-heading text-4xl font-bold text-foreground mb-3">
              {content.title}
            </h1>
            <p className="text-sm text-muted-foreground mb-10">{content.lastUpdated}</p>

            <nav
              aria-label={content.tocTitle}
              className="mb-12 rounded-2xl border border-border bg-card p-5"
            >
              <h2 className="font-heading text-lg font-semibold text-foreground mb-3">
                {content.tocTitle}
              </h2>
              <ol className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
                {content.sections.map((s) => (
                  <li key={s.id}>
                    <a
                      href={`#${s.id}`}
                      className="text-muted-foreground hover:text-accent transition-colors"
                    >
                      {s.title}
                    </a>
                  </li>
                ))}
              </ol>
            </nav>

            <div className="prose prose-lg max-w-none text-muted-foreground">
              {content.blocks.map((block, index) => {
                switch (block.type) {
                  case "lead":
                    return (
                      <p key={index} className="lead">
                        {block.text}
                      </p>
                    );
                  case "h2":
                    return (
                      <h2 key={index} id={block.id} className="text-foreground scroll-mt-28">
                        {block.text}
                      </h2>
                    );
                  case "p":
                    return (
                      <p key={index}>
                        {block.label && (
                          <strong className="text-foreground">{block.label} </strong>
                        )}
                        {block.text}
                      </p>
                    );
                  case "ul":
                    return (
                      <ul key={index}>
                        {block.items.map((item, i) => (
                          <li key={i}>
                            {item.label && (
                              <strong className="text-foreground">{item.label} </strong>
                            )}
                            {item.text}
                          </li>
                        ))}
                      </ul>
                    );
                  case "cookieButton":
                    return (
                      <p key={index}>
                        <button
                          type="button"
                          onClick={() =>
                            window.dispatchEvent(new Event(COOKIE_SETTINGS_EVENT))
                          }
                          className="text-accent underline underline-offset-2 hover:opacity-80"
                        >
                          {block.text}
                        </button>
                      </p>
                    );
                  case "contact":
                    return (
                      <ul key={index}>
                        <li>
                          {labels.email}{" "}
                          <a
                            href="mailto:Support@Adam-Safety.com"
                            className="text-accent underline underline-offset-2"
                          >
                            Support@Adam-Safety.com
                          </a>
                        </li>
                        <li>
                          {labels.whatsapp}{" "}
                          <a
                            href="https://wa.me/972528300088"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-accent underline underline-offset-2"
                          >
                            052-830-0088
                          </a>
                        </li>
                      </ul>
                    );
                  default:
                    return null;
                }
              })}
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
